import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import mixpanel from 'mixpanel-browser';
import {
  Product, resolveImg, typeIcon,
} from './productsData';

const PURPLE = '#3700B3';
const CARD_BORDER = '1px solid #ECEAF2';
const CARD_SHADOW = '0 1px 2px rgba(20,10,40,0.04), 0 6px 18px rgba(40,20,90,0.05)';

function ProductCard({ product, viewLabel, species }: { product: Product, viewLabel: string, species: string }) {
  const [failed, setFailed] = useState(false);
  const src = resolveImg(product.name);
  const showImg = !!src && !failed;
  const TypeIcon = typeIcon(product.type);

  return (
    <Box
      sx={{
        background: '#fff',
        borderRadius: '16px',
        border: CARD_BORDER,
        boxShadow: CARD_SHADOW,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      {/* image area — placeholder always rendered behind the photo */}
      <Box sx={{
        position: 'relative', aspectRatio: '247 / 176', flexShrink: 0, background: '#F7F6FB', overflow: 'hidden',
      }}
      >
        <Box sx={{
          position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
        >
          <TypeIcon sx={{ fontSize: { xs: 40, lg: 54 }, color: 'rgba(55,0,179,0.26)' }} />
          <Box
            component="img"
            src="/hub/petcademy-icon-purple.svg"
            alt=""
            aria-hidden="true"
            sx={{
              position: 'absolute', right: 10, bottom: 9, height: 15, opacity: 0.3,
            }}
          />
        </Box>
        {showImg && (
          <Box
            component="img"
            src={src}
            alt={product.name}
            onError={() => setFailed(true)}
            sx={{
              position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover',
            }}
          />
        )}
      </Box>

      {/* body */}
      <Box sx={{
        p: { xs: '11px 12px 13px', lg: '14px 16px 16px' }, display: 'flex', flexDirection: 'column', flex: 1,
      }}
      >
        <Typography
          sx={{
            fontFamily: 'Lexend',
            fontWeight: 700,
            fontSize: { xs: 13, lg: 15.5 },
            lineHeight: 1.32,
            minHeight: '3.96em',
            color: '#1A1430',
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 3,
            overflow: 'hidden',
          }}
        >
          {product.name}
        </Typography>
        {product.price && (
          <Typography sx={{
            fontFamily: 'Lexend', fontWeight: 500, fontSize: { xs: 12.5, lg: 13.5 }, color: 'rgba(0,0,0,0.60)', mt: '6px',
          }}
          >
            {product.price}
          </Typography>
        )}
        <Box
          component="a"
          href={product.link || '#'}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            mixpanel.track('recommended_product_click', {
              product_name: product.name,
              product_category: product.focus,
              species,
              link: product.link,
            });
          }}
          sx={{ mt: 'auto', pt: '12px', textDecoration: 'none' }}
        >
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '7px',
            background: '#fff',
            color: PURPLE,
            border: '1px solid #C9BCEE',
            borderRadius: '10px',
            p: { xs: '8px 10px', lg: '10px 14px' },
            fontFamily: 'Lexend',
            fontWeight: 600,
            fontSize: { xs: 13, lg: 14 },
            '&:hover': { background: 'rgba(55,0,179,0.04)' },
          }}
          >
            {viewLabel}
            <OpenInNewIcon sx={{ fontSize: { xs: 15, lg: 17 } }} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default ProductCard;
